import type { ReactNode } from 'react'

export type RuleListRowIconKind = 'default' | 'event' | 'empty' | 'custom'

export type RuleListRowProps = {
  icon: ReactNode
  iconKind?: RuleListRowIconKind
  label: ReactNode
  value?: ReactNode
  onRemove?: () => void
  draft?: boolean
  className?: string
}

const ICON_PILL_BASE =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-md'

const iconPillClasses = (kind: RuleListRowIconKind): string => {
  switch (kind) {
    case 'event':
      return `${ICON_PILL_BASE} bg-[#F0F9FF] text-[#0369A1]`
    case 'empty':
      return `${ICON_PILL_BASE} border border-dashed border-doubleNickel-gray-300 bg-transparent text-doubleNickel-gray-400`
    case 'custom':
      // Caller's icon node renders its own pill (e.g. ActionIconBadge brings
      // its own per-action-type colors). We just provide the slot — no chrome.
      return 'flex shrink-0 items-center justify-center'
    case 'default':
    default:
      return `${ICON_PILL_BASE} bg-doubleNickel-gray-50 text-doubleNickel-gray-700`
  }
}

const ROW_BASE =
  'group/rule-row relative flex w-full items-center gap-2.5 rounded-[7px] border bg-white px-2.5 py-2 transition-colors'

const rowVariantClasses = (draft: boolean): string =>
  draft
    ? 'border-dashed border-doubleNickel-gray-200 hover:border-doubleNickel-brand-500 hover:bg-[rgba(28,147,215,0.04)]'
    : 'border-solid border-doubleNickel-gray-200 hover:border-doubleNickel-gray-300 hover:bg-[#FAFBFC]'

const RuleListRow = ({
  icon,
  iconKind,
  label,
  value,
  onRemove,
  draft = false,
  className,
}: RuleListRowProps) => {
  const resolvedIconKind: RuleListRowIconKind =
    iconKind ?? (draft ? 'empty' : 'default')

  const removeOpacityClass = draft
    ? 'opacity-100'
    : 'opacity-0 group-hover/rule-row:opacity-100 focus:opacity-100'

  return (
    <div
      className={`${ROW_BASE} ${rowVariantClasses(draft)}${
        className ? ` ${className}` : ''
      }`}
    >
      <span className={iconPillClasses(resolvedIconKind)} aria-hidden>
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="min-w-0 truncate text-[12px] font-semibold leading-tight text-doubleNickel-gray-900">
          {label}
        </div>
        {value !== undefined && value !== null && value !== '' ? (
          <div className="min-w-0 truncate text-[11px] font-normal leading-tight text-doubleNickel-gray-500">
            {value}
          </div>
        ) : null}
      </div>
      {onRemove ? (
        <button
          type="button"
          aria-label="Remove"
          onClick={onRemove}
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-doubleNickel-gray-400 transition-opacity hover:bg-doubleNickel-gray-50 hover:text-doubleNickel-gray-700 ${removeOpacityClass}`}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="1.5" y1="1.5" x2="8.5" y2="8.5" />
            <line x1="8.5" y1="1.5" x2="1.5" y2="8.5" />
          </svg>
        </button>
      ) : null}
    </div>
  )
}

export default RuleListRow
